import React, { useEffect, useState, useMemo } from 'react';
import {
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Box,
    IconButton,
    Tooltip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    InputAdornment,
    TableSortLabel,
    TablePagination,
    Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TitleBar from '../../components/TitleBar/TitleBar';
import { getReviewEntities, deleteReviewEntity, getShowOtherUsersReviewEntities, getMyReviewEntities, getReviewEntityByReviewee } from '../../services/api';
import { getUser } from '../../services/auth';
import { Visibility, Add, Delete, Search, ArrowBack, FiberManualRecord, Edit, Cancel } from '@mui/icons-material';
import { AddReviewEntity } from './AddReviewEntity';

interface ReviewAttribute {
    id: number;
    name: string;
    description: string;
    value: string;
    review_entity_id: number;
}

interface ReviewEntity {
    id: number;
    name: string;
    description: string;
    org_entity_code?: string;
    review_entity_type_id: number;
    review_entity_type?: {
        id: number;
        name: string;
        display_label: string;
        category: string;
        description: string;
    };
    reviewers: number[];
    reviewees: number[];
    owner?: number;
    owner_name?: string;
    owner_display_name?: string;
    status?: string;
    valid_from?: string;
    valid_till?: string;
    created_by: number;
    updated_by: number;
    created_at: string;
    updated_at: string;
    review_attributes?: ReviewAttribute[];
    attributes?: ReviewAttribute[];
    reviewer_names: string[];
    reviewee_names: string[];
}

type SortField = 'name' | 'description' | 'org_entity_code';
type SortDirection = 'asc' | 'desc';

const ReviewEntityManagement: React.FC = () => {
    const [entities, setEntities] = useState<ReviewEntity[]>([]);
    const [error, setError] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [entityToDelete, setEntityToDelete] = useState<ReviewEntity | null>(null);
    const [userId, setUserId] = useState<number | null>(null);
    const [showOtherUsersReviewEntities, setShowOtherUsersReviewEntities] = useState<boolean>(true);
    const navigate = useNavigate();

    const [sortField, setSortField] = useState<SortField>('name');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [filterName, setFilterName] = useState<string>('');
    const [filterType, setFilterType] = useState<string>('');
    const [filterDescription, setFilterDescription] = useState<string>('');
    const [filterEntityCode, setFilterEntityCode] = useState<string>('');
    const [filterOwner, setFilterOwner] = useState<string>('');
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(25);

    // Initialize user ID
    useEffect(() => {
        const user = getUser();
        if (user?.id) {
            console.log('DEBUG: Setting userId to:', user.id);
            setUserId(user.id);
        } else {
            setError('User ID not found');
        }
    }, []);

    const fetchEntities = async () => {
        if (!userId) return;
        
        setLoading(true);
        setError('');
        
        try {
            // First get the config setting
            console.log('DEBUG: Fetching config to determine which API to use');
            const configResponse = await getShowOtherUsersReviewEntities();
            const configValue = configResponse.data.show_other_users_review_entities;
            console.log('DEBUG: show_other_users_review_entities config value:', configValue);
            setShowOtherUsersReviewEntities(configValue);
            
            // Then fetch entities based on config
            let response;
            if (configValue) {
                console.log('DEBUG: Config is true, fetching all review entities with userId:', userId);
                response = await getReviewEntityByReviewee(userId);
            } else {
                console.log('DEBUG: Config is false, fetching only my review entities');
                response = await getMyReviewEntities();
            }
            
            console.log('DEBUG: Entity response received:', response);
            
            // Check if we have a valid array in response.data
            if (!response.data || !Array.isArray(response.data)) {
                console.error('Invalid response format:', response.data);
                setError('Invalid response format from server');
                return;
            }
            
            // Process the data to ensure proper type conversion between API type and component type
            const processedEntities = response.data.map(entity => {
                // Convert from API type to component type with proper type assertion
                const convertedEntity = entity as unknown as ReviewEntity;
                
                // Ensure review_attributes exists on all entities
                if (!convertedEntity.review_attributes) {
                    convertedEntity.review_attributes = [];
                }
                
                return convertedEntity;
            });
            
            setEntities(processedEntities);
            console.log('DEBUG: Entities set to:', processedEntities);
        } catch (err) {
            setError('Failed to load review entities');
            console.error('DEBUG: Error fetching review entities:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userId) {
            fetchEntities();
        }
    }, [userId]);

    const handleViewDetails = (id: number) => {
        navigate(`/review-entities/${id}`);
    };

    const handleAddSuccess = () => {
        setOpenAddDialog(false);
        fetchEntities();
    };

    const handleDeleteClick = (entity: ReviewEntity) => {
        setEntityToDelete(entity);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!entityToDelete) return;
        try {
            await deleteReviewEntity(entityToDelete.id);
            setDeleteDialogOpen(false);
            setEntityToDelete(null);
            fetchEntities();
        } catch (err) {
            console.error('Error deleting review entity:', err);
            setError('Failed to delete review entity');
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setEntityToDelete(null);
    };

    const handleRequestSort = (field: SortField) => {
        const isAsc = sortField === field && sortDirection === 'asc';
        setSortDirection(isAsc ? 'desc' : 'asc');
        setSortField(field);
    };

    const getStatusIcon = (status: string | undefined) => {
        const statusValue = status || 'ACTIVE';
        switch (statusValue) {
            case 'ACTIVE':
                return <FiberManualRecord sx={{ color: 'success.main', fontSize: 16 }} />;
            case 'DRAFT':
                return <Edit sx={{ color: 'warning.main', fontSize: 20 }} />;
            case 'IN-ACTIVE':
                return <Cancel sx={{ color: 'error.main', fontSize: 20 }} />;
            default:
                return <FiberManualRecord sx={{ color: 'success.main', fontSize: 16 }} />;
        }
    };

    const filteredAndSortedEntities = useMemo(() => {
        const filteredEntities = entities.filter(entity => {
            const nameMatch = filterName === '' || entity.name.toLowerCase().includes(filterName.toLowerCase());
            const descriptionMatch = filterDescription === '' || entity.description.toLowerCase().includes(filterDescription.toLowerCase());
            const typeMatch = filterType === '' || 
                (entity.review_entity_type?.display_label && entity.review_entity_type.display_label.toLowerCase().includes(filterType.toLowerCase()));
            const entityCodeMatch = filterEntityCode === '' || 
                (entity.org_entity_code && entity.org_entity_code.toLowerCase().includes(filterEntityCode.toLowerCase()));
            const ownerMatch = filterOwner === '' || 
                ((entity.owner_display_name && entity.owner_display_name.toLowerCase().includes(filterOwner.toLowerCase())) ||
                 (entity.owner_name && entity.owner_name.toLowerCase().includes(filterOwner.toLowerCase())));
            return nameMatch && descriptionMatch && typeMatch && entityCodeMatch && ownerMatch;
        });

        return [...filteredEntities].sort((a, b) => {
            let aValue: string;
            let bValue: string;
            
            if (sortField === 'name' || sortField === 'description' || sortField === 'org_entity_code') {
                aValue = String(a[sortField] ?? '').toLowerCase();
                bValue = String(b[sortField] ?? '').toLowerCase();
            } else {
                aValue = '';
                bValue = '';
            }
            
            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    }, [entities, filterName, filterDescription, filterType, filterEntityCode, filterOwner, sortField, sortDirection]);

    // Reset to first page when any filter changes
    useEffect(() => {
        setPage(0);
    }, [filterName, filterDescription, filterType, filterEntityCode, filterOwner]);

    const paginatedEntities = useMemo(() => {
        const start = page * rowsPerPage;
        return filteredAndSortedEntities.slice(start, start + rowsPerPage);
    }, [filteredAndSortedEntities, page, rowsPerPage]);

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <>
            <TitleBar />
            <Container maxWidth="xl">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 2 }}>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                            onClick={() => navigate('/home')}
                            sx={{
                                border: '1px solid #ccc',
                                borderRadius: 2,
                                p: 1,
                                backgroundColor: '#f5f5f5',
                                '&:hover': {
                                    backgroundColor: '#e0e0e0',
                                },
                            }}
                        >
                            <ArrowBack />
                        </IconButton>
                        <Typography variant="h6" fontWeight="bold" sx={{ ml: 1 }}>
                            Review Entities
                        </Typography>
                    </Box>

                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => setOpenAddDialog(true)}
                    >
                        Add Entity
                    </Button>
                </Box>

                <Paper sx={{ p: 2 }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table sx={{ minWidth: 700 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <TextField
                                            placeholder="Filter by name"
                                            value={filterName}
                                            onChange={(e) => setFilterName(e.target.value)}
                                            size="small"
                                            fullWidth
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Search fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            placeholder="Filter by description"
                                            value={filterDescription}
                                            onChange={(e) => setFilterDescription(e.target.value)}
                                            size="small"
                                            fullWidth
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Search fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            placeholder="Filter by type"
                                            value={filterType}
                                            onChange={(e) => setFilterType(e.target.value)}
                                            size="small"
                                            fullWidth
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Search fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            placeholder="Filter by entity code"
                                            value={filterEntityCode}
                                            onChange={(e) => setFilterEntityCode(e.target.value)}
                                            size="small"
                                            fullWidth
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Search fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            placeholder="Filter by owner"
                                            value={filterOwner}
                                            onChange={(e) => setFilterOwner(e.target.value)}
                                            size="small"
                                            fullWidth
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Search fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell />
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortField === 'name'}
                                            direction={sortField === 'name' ? sortDirection : 'asc'}
                                            onClick={() => handleRequestSort('name')}
                                        >
                                            <strong>Name</strong>
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortField === 'description'}
                                            direction={sortField === 'description' ? sortDirection : 'asc'}
                                            onClick={() => handleRequestSort('description')}
                                        >
                                            <strong>Description</strong>
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell><strong>Type</strong></TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortField === 'org_entity_code'}
                                            direction={sortField === 'org_entity_code' ? sortDirection : 'asc'}
                                            onClick={() => handleRequestSort('org_entity_code')}
                                        >
                                            <strong>Entity Code</strong>
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell><strong>Owner</strong></TableCell>
                                    <TableCell><strong>Actions</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedEntities.map((entity) => (
                                    <TableRow key={entity.id}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Tooltip title={entity.status || 'ACTIVE'}>
                                                    {getStatusIcon(entity.status)}
                                                </Tooltip>
                                                {entity.name}
                                            </Box>
                                        </TableCell>
                                        <TableCell>{entity.description}</TableCell>
                                        <TableCell>{entity.review_entity_type?.display_label || 'Unknown'}</TableCell>
                                        <TableCell>{entity.org_entity_code ? entity.org_entity_code : "Not Available"}</TableCell>
                                        <TableCell sx={{ minWidth: 100 }}>{entity.owner_display_name || entity.owner_name || '-'}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                                                <Tooltip title="View Details">
                                                    <IconButton size="small" onClick={() => handleViewDetails(entity.id)}>
                                                        <Visibility />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton size="small" onClick={() => handleDeleteClick(entity)} color="error">
                                                        <Delete />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={filteredAndSortedEntities.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </Paper>
            </Container>

            <AddReviewEntity
                open={openAddDialog}
                onClose={() => setOpenAddDialog(false)}
                onSuccess={handleAddSuccess}
            />

            <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
                <DialogTitle>Delete Review Entity</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete "{entityToDelete?.name}"?
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} color="inherit">Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export { ReviewEntityManagement };
